/**
 * ProgressBar Component
 * Displays quiz progress with linear or step-based indicator
 */

import React from 'react';
import { Box, LinearProgress, Typography, Stepper, Step, StepLabel } from '@mui/material';

interface ProgressBarProps {
  currentQuestion: number;
  totalQuestions: number;
  currentQuestionNumber?: number;
  quizName?: string;
  variant?: 'linear' | 'stepper';
  showLabel?: boolean;
  showQuestionNumber?: boolean;
  showBar?: boolean;
}

function ProgressBar({
  currentQuestion,
  totalQuestions,
  currentQuestionNumber = currentQuestion + 1,
  quizName,
  variant = 'linear',
  showLabel = true,
  showQuestionNumber = true,
  showBar = true,
}: ProgressBarProps): JSX.Element {
  const progress = totalQuestions > 0 ? (currentQuestionNumber / totalQuestions) * 100 : 0;

  if (variant === 'stepper') {
    return (
      <Stepper activeStep={currentQuestion} sx={{ mb: 4 }}>
        {Array.from({ length: Math.min(totalQuestions, 5) }).map((_, index) => (
          <Step key={index}>
            <StepLabel>{index + 1}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  const questionLabel = `QUESTION ${currentQuestionNumber} OF ${totalQuestions}`;
  const headerLabel = quizName
    ? showQuestionNumber ? `${quizName.toUpperCase()} | ${questionLabel}` : quizName.toUpperCase()
    : questionLabel;

  return (
    <Box className="cla-quiz-progress" sx={{ mb: 3 }}>
      {showLabel && (
        <Typography
        className="cla-quiz-progress-label"
          variant="caption"
          sx={{
            display: 'block',
            mb: 2,
            fontSize: '0.85rem',
            fontWeight: 700,
            color: '#00A9B5',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {headerLabel}
        </Typography>
      )}
      {showBar && (
        <LinearProgress
          className="cla-quiz-progress-bar"
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            borderRadius: 2,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              backgroundColor: '#00A9B5',
            },
          }}
        />
      )}
    </Box>
  );
}

export default ProgressBar;
